// Shared UI primitives — the ONE correct version of each component, styled to the
// Rosemoor mockup. Migrate hand-rolled markup across screens onto these.
import { useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/lib/icons";

// ---------- Button ----------
// primary = filled maroon, secondary = outlined, ghost = text, danger = maroon outline.
export function Button({ variant = "primary", className = "", children, ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-maroon text-cream hover:bg-maroon/90",
    secondary: "bg-transparent border border-ink/25 text-ink hover:border-maroon/50 hover:text-maroon",
    gold: "bg-gold text-ink hover:bg-gold/90",
    olive: "bg-dark-olive text-cream hover:bg-dark-olive/90",
    ghost: "bg-transparent text-ink/70 hover:text-ink hover:bg-ink/5",
    danger: "bg-transparent border border-maroon/40 text-maroon hover:bg-maroon/5",
  };
  return (
    <button className={`${base} ${styles[variant] || styles.primary} ${className}`} {...props}>
      {children}
    </button>
  );
}

// ---------- Field (label above a bordered input) ----------
export function Field({ label, hint, className = "", children, ...input }) {
  return (
    <div className={className}>
      {label && <label className="text-[13px] font-semibold text-ink/75 block mb-1">{label}</label>}
      {children || (
        <input
          className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-maroon/50"
          {...input}
        />
      )}
      {hint && <p className="text-[12px] text-ink/50 mt-1">{hint}</p>}
    </div>
  );
}

export function Textarea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 text-[15px] resize-none focus:outline-none focus:border-maroon/50 ${className}`}
      {...props}
    />
  );
}

// ---------- Input (bordered text input, matches Field's default) ----------
export function Input({ className = "", ...props }) {
  return (
    <input
      className={`w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-maroon/50 ${className}`}
      {...props}
    />
  );
}

// ---------- Select (native select, styled to match inputs) ----------
export function Select({ className = "", children, ...props }) {
  return (
    <select
      className={`w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-maroon/50 ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

// ---------- Card (rounded, padded, subtle border, optional colored top accent) ----------
export function Card({ accent, className = "", style = {}, children, ...props }) {
  const s = accent ? { borderTopWidth: 5, borderTopColor: accent, ...style } : style;
  return (
    <div className={`bg-cream border border-ink/15 rounded-xl p-5 ${className}`} style={s} {...props}>
      {children}
    </div>
  );
}

// ---------- Toggle (pill: olive when on, gray when off) ----------
export function Toggle({ checked, onChange, disabled, "data-testid": testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-dark-olive" : "bg-ink/25"}`}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-cream shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
  );
}

// ---------- Modal / Drawer (dark header + rounded close, cream body, footer actions) ----------
export function Modal({ open, onClose, title, children, footer, "data-testid": testId }) {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="bg-cream w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-ink/15 shadow-xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-ink text-cream px-5 py-3.5 flex items-center justify-between shrink-0">
          <h3 className="font-zilla text-lg font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center">
            <Icon name="X" className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
        {footer && <div className="p-4 border-t border-ink/10 flex gap-2 justify-end shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// ---------- Drawer (right-side slide-over: dark header + close, cream body) ----------
export function Drawer({ open, onClose, title, eyebrow, children, footer, width = "max-w-xl", "data-testid": testId }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-ink/50 flex justify-end" onClick={onClose}>
      <div className={`w-full ${width} bg-warm-cream h-full overflow-y-auto shadow-2xl flex flex-col`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="sticky top-0 bg-ink text-cream px-6 py-4 flex items-center justify-between z-10 shrink-0">
          <div>
            {eyebrow && <div className="text-[12px] uppercase tracking-widest text-gold font-bold">{eyebrow}</div>}
            <div className="font-zilla text-lg font-semibold">{title}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center shrink-0"><Icon name="X" className="w-4 h-4" /></button>
        </div>
        <div className="p-6 flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-ink/10 flex gap-2 justify-end shrink-0 bg-warm-cream">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// ---------- StatusPill (small rounded, colored to meaning) ----------
const PILL_TONES = {
  green: "bg-dark-olive/15 text-dark-olive",
  olive: "bg-olive/20 text-dark-olive",
  gold: "bg-gold/25 text-gold-ink",
  maroon: "bg-maroon/12 text-maroon",
  gray: "bg-ink/10 text-ink/60",
};
export function StatusPill({ tone = "gray", className = "", children, ...rest }) {
  return (
    <span {...rest} className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full font-semibold ${PILL_TONES[tone] || PILL_TONES.gray} ${className}`}>
      {children}
    </span>
  );
}
